// 应用更新 - 前端桥接模块（v11 简化版）
//
// 关键设计：
//   - 不依赖 Capacitor 自定义插件注册系统（经常失败）
//   - 使用 navigator.userAgent 检测 Android 环境（最可靠）
//   - 原生层 MainActivity.injectAppInfo() 注入 window.__APP_INFO__
//   - 原生层 MainActivity 暴露 window.__NATIVE_API__ 用于下载/安装
//
// 调用顺序：
//   getNativeVersion()  -> 设置页显示版本号
//   checkUpdate()       -> 对比服务器版本
//   downloadApk()       -> 下载新版本
//   installApk()        -> 调用系统安装程序

import { api } from './api';

// ============================================================
// 类型定义
// ============================================================

export interface UpdateCheckRequest {
  currentVersion: string;
  platform?: string;
}

export interface UpdateCheckResponse {
  hasUpdate: boolean;
  latestVersion?: string;
  versionCode?: number;
  downloadUrl?: string;
  releaseNotes?: string;
  isForce?: boolean;
  fileSize?: number;
  checksum?: string;
  platform?: string;
  publishedAt?: string;
  message?: string;
}

export interface NativeVersionInfo {
  version: string;
  versionCode: number;
  packageName: string;
  platform: string;
}

export interface DeviceIdInfo {
  deviceId: string;
  deviceName: string;
  androidVersion?: string;
  sdkInt?: number;
  error?: string;
}

export interface DownloadProgressEvent {
  downloadId: number;
  status: number;
  bytesDownloaded: number;
  bytesTotal: number;
  percent: number;
}

// ============================================================
// 环境检测（最可靠的方案）
// ============================================================

/**
 * 判断当前是否运行在 Android APK 中
 * 同时检查：
 *   1. navigator.userAgent 包含 "Android"
 *   2. window.__APP_INFO__ 存在（原生层注入）
 *   3. window.Capacitor 存在（可选）
 */
function isAndroidAPK(): boolean {
  try {
    // 检查 1：userAgent
    const ua = navigator.userAgent || '';
    const hasAndroidUA = ua.indexOf('Android') >= 0 || ua.indexOf('android') >= 0;

    // 检查 2：原生层注入的 __APP_INFO__
    const hasInjected = !!(window as any).__APP_INFO__;

    // 检查 3：Capacitor 对象（可选）
    const hasCapacitor = !!(window as any).Capacitor && !!(window as any).Capacitor.Plugins;

    console.log('[AppUpdate] 环境检测: userAgent=Android:' + hasAndroidUA + ', __APP_INFO__:' + hasInjected + ', Capacitor:' + hasCapacitor);

    return hasAndroidUA || hasInjected || hasCapacitor;
  } catch (e) {
    console.warn('[AppUpdate] 环境检测异常:', e);
    return false;
  }
}

// ============================================================
// 核心功能
// ============================================================

/**
 * 获取当前应用版本号
 * 优先级：
 *   1. window.__APP_INFO__（原生层注入，最可靠）
 *   2. window.Capacitor.Plugins.App.getInfo()（Capacitor 原生）
 *   3. 在 Android 环境下 -> 返回硬编码 1.0.0 + android
 *   4. 浏览器环境 -> 返回 0.0.0 + web
 */
export async function getNativeVersion(): Promise<NativeVersionInfo> {
  console.log('%c===== [AppUpdate] getNativeVersion() 开始 =====', 'color:#2563eb;font-weight:bold;font-size:14px;');
  console.log('[AppUpdate] navigator.userAgent:', navigator.userAgent);

  // === 方案 1：原生层直接注入的 window.__APP_INFO__ ===
  try {
    const injected = (window as any).__APP_INFO__;
    if (injected && injected.version) {
      console.log('[AppUpdate] ✅ 方案1: window.__APP_INFO__ 成功:', JSON.stringify(injected));
      return {
        version: String(injected.version),
        versionCode: Number(injected.versionCode) || 1000000,
        packageName: String(injected.packageName || 'com.nzx.video'),
        platform: String(injected.platform || 'android'),
      };
    }
  } catch (e) {
    console.warn('[AppUpdate] 方案1异常:', e);
  }

  // === 方案 2：Capacitor 原生 App.getInfo() ===
  try {
    const cap = (window as any).Capacitor;
    if (cap && cap.Plugins && cap.Plugins.App && typeof cap.Plugins.App.getInfo === 'function') {
      console.log('[AppUpdate] 方案2: Capacitor.Plugins.App.getInfo()');
      const ret = await cap.Plugins.App.getInfo();
      console.log('[AppUpdate] 方案2返回:', JSON.stringify(ret));
      if (ret && ret.version) {
        return {
          version: String(ret.version),
          versionCode: parseInt(String(ret.build || ret.versionCode || '1'), 10) || 1,
          packageName: String(ret.id || ret.packageName || 'com.nzx.video'),
          platform: 'android',
        };
      }
    }
  } catch (e) {
    console.warn('[AppUpdate] 方案2异常:', e);
  }

  // === 方案 3：Capacitor 自定义插件 AppUpdate ===
  try {
    const cap = (window as any).Capacitor;
    const appUpdate = cap?.Plugins?.AppUpdate;
    if (appUpdate && typeof appUpdate.getCurrentVersion === 'function') {
      console.log('[AppUpdate] 方案3: Capacitor.Plugins.AppUpdate.getCurrentVersion()');
      const ret = await appUpdate.getCurrentVersion();
      console.log('[AppUpdate] 方案3返回:', JSON.stringify(ret));
      if (ret && ret.version) {
        return {
          version: String(ret.version),
          versionCode: Number(ret.versionCode) || 1,
          packageName: String(ret.packageName || 'com.nzx.video'),
          platform: String(ret.platform || 'android'),
        };
      }
    }
  } catch (e) {
    console.warn('[AppUpdate] 方案3异常:', e);
  }

  // === 方案 4：判断是否为 Android APK 环境，返回 android 平台 ===
  if (isAndroidAPK()) {
    console.log('[AppUpdate] 方案4: 检测到 Android 环境，返回硬编码版本 1.0.0 (android)');
    return {
      version: '1.0.0',
      versionCode: 1000000,
      packageName: 'com.nzx.video',
      platform: 'android',
    };
  }

  // === 方案 5：浏览器环境 ===
  console.log('[AppUpdate] 方案5: 浏览器环境，返回 0.0.0 (web)');
  return {
    version: '0.0.0',
    versionCode: 1,
    packageName: 'com.nzx.video',
    platform: 'web',
  };
}

/**
 * 🔴 尝试调用 window.NativeBridge 的旧版同步 downloadApk（已废弃，新版用 startDownload）
 * 这里保留只是为了兼容极端情况
 */
function tryLegacyNativeBridgeDownload(url: string, filename: string): string | null {
  try {
    const nb = (window as any).NativeBridge;
    if (nb && typeof nb.downloadApk === 'function' && nb.downloadApk.length >= 2) {
      console.log('[AppUpdate] ⚠️ 尝试旧的 NativeBridge.downloadApk（同步，已废弃）');
      const ret = nb.downloadApk(url, filename);
      if (typeof ret === 'string') {
        const obj = JSON.parse(ret);
        if (obj.success && obj.filePath) return String(obj.filePath);
      }
    }
  } catch (e) {
    console.warn('[AppUpdate] legacy downloadApk 异常:', e);
  }
  return null;
}

/**
 * 下载 APK（异步，通过回调获取进度和结果）
 * 优先级：
 *   1. window.__NATIVE_API__.downloadApk() — Promise 风格，进度通过 onProgress 回调
 *   2. window.NativeBridge.startDownload() + onDownloadProgress/onDownloadComplete 回调
 *   3. window.NativeBridge.downloadApk() 旧版同步（已废弃）
 *   4. Capacitor.Plugins.AppUpdate
 */
export async function downloadApk(
  url: string,
  filename?: string,
  onProgress?: (progress: DownloadProgressEvent) => void
): Promise<string> {
  console.log('%c===== [AppUpdate] downloadApk() 开始（异步模式） =====', 'color:#2563eb;font-weight:bold;font-size:14px;');
  console.log('[AppUpdate] URL:', url);
  console.log('[AppUpdate] filename:', filename);

  const realFilename = filename || 'update.apk';

  // === 方案 1：window.__NATIVE_API__.downloadApk() — Promise 风格 ===
  try {
    const nativeApi = (window as any).__NATIVE_API__;
    if (nativeApi && typeof nativeApi.downloadApk === 'function') {
      console.log('[AppUpdate] ✅ 方案1: window.__NATIVE_API__.downloadApk()');
      const result: any = await nativeApi.downloadApk({ url, filename: realFilename, onProgress });
      console.log('[AppUpdate] 方案1返回:', JSON.stringify(result));
      if (result && result.success && result.filePath) {
        return String(result.filePath);
      }
      if (result && result.filePath) {
        // 进度回调已正常处理，完成回调可能走的是 type:complete 分支
        return String(result.filePath);
      }
      throw new Error(result?.message || '下载失败');
    }
  } catch (e: any) {
    console.warn('[AppUpdate] 方案1异常:', e?.message || e);
    // 不 throw，继续尝试其他方案
  }

  // === 方案 2：直接调用 NativeBridge.startDownload() 并监听回调 ===
  try {
    const nb = (window as any).NativeBridge;
    if (nb && typeof nb.startDownload === 'function') {
      console.log('[AppUpdate] ✅ 方案2: NativeBridge.startDownload() + 回调');
      return await new Promise<string>((resolve, reject) => {
        const cbId = 'dl_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
        const api = (window as any).__NATIVE_API__;
        if (api && api._cbs) {
          api._cbs[cbId] = (payload: any) => {
            if (payload.type === 'progress' && onProgress) {
              onProgress({
                downloadId: -1,
                status: 1,
                bytesDownloaded: payload.bytesDownloaded || 0,
                bytesTotal: payload.bytesTotal || 0,
                percent: payload.percent || 0,
              });
            } else if (payload.type === 'complete') {
              delete api._cbs[cbId];
              if (payload.success && payload.filePath) {
                resolve(String(payload.filePath));
              } else {
                reject(new Error(payload.message || '下载失败'));
              }
            }
          };
        }
        try {
          const ret = nb.startDownload(url, realFilename, cbId);
          const obj = JSON.parse(ret);
          if (!obj.success) {
            delete api._cbs[cbId];
            reject(new Error(obj.message || '下载启动失败'));
          }
        } catch (e: any) {
          if (api && api._cbs) delete api._cbs[cbId];
          reject(e);
        }
      });
    }
  } catch (e: any) {
    console.warn('[AppUpdate] 方案2异常:', e?.message || e);
  }

  // === 方案 3：旧版同步（兜底） ===
  try {
    const result = tryLegacyNativeBridgeDownload(url, realFilename);
    if (result) {
      console.log('[AppUpdate] ✅ 方案3（旧同步）成功:', result);
      return result;
    }
  } catch (e: any) {
    console.warn('[AppUpdate] 方案3异常:', e?.message || e);
  }

  // === 方案 4：Capacitor 自定义插件 ===
  try {
    const cap = (window as any).Capacitor;
    const appUpdate = cap?.Plugins?.AppUpdate;
    if (appUpdate && typeof appUpdate.downloadApk === 'function') {
      console.log('[AppUpdate] ✅ 方案4: Capacitor.Plugins.AppUpdate.downloadApk()');
      const ret = await appUpdate.downloadApk({ url, filename: realFilename });
      if (ret && ret.filePath) return String(ret.filePath);
      if (ret && ret.uri) return String(ret.uri).replace(/^file:\/\//, '');
      throw new Error(ret?.message || '下载失败');
    }
  } catch (e: any) {
    console.warn('[AppUpdate] 方案4异常:', e?.message || e);
    throw e;
  }

  console.error('[AppUpdate] ❌ 没有可用的下载方式');
  throw new Error('未检测到原生下载接口，请重启 App 后重试');
}

/**
 * 安装 APK（异步）
 * 优先级：
 *   1. window.__NATIVE_API__.installApk() — Promise 风格
 *   2. window.NativeBridge.startInstall() + onInstallResult 回调
 *   3. Capacitor.Plugins.AppUpdate
 */
export async function installApk(filePath: string): Promise<{ success: boolean; needPermission?: boolean; message?: string }> {
  console.log('%c===== [AppUpdate] installApk() 开始（异步模式） =====', 'color:#2563eb;font-weight:bold;font-size:14px;');
  console.log('[AppUpdate] filePath:', filePath);

  // === 方案 1：window.__NATIVE_API__.installApk() ===
  try {
    const nativeApi = (window as any).__NATIVE_API__;
    if (nativeApi && typeof nativeApi.installApk === 'function') {
      console.log('[AppUpdate] ✅ 方案1: window.__NATIVE_API__.installApk()');
      const ret: any = await nativeApi.installApk({ filePath });
      console.log('[AppUpdate] 方案1返回:', JSON.stringify(ret));
      return {
        success: Boolean(ret?.success),
        needPermission: Boolean(ret?.needPermission),
        message: ret?.message,
      };
    }
  } catch (e: any) {
    console.warn('[AppUpdate] 方案1异常:', e?.message || e);
  }

  // === 方案 2：NativeBridge.startInstall() + 回调 ===
  try {
    const nb = (window as any).NativeBridge;
    if (nb && typeof nb.startInstall === 'function') {
      console.log('[AppUpdate] ✅ 方案2: NativeBridge.startInstall() + 回调');
      return await new Promise((resolve) => {
        const cbId = 'inst_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
        const api = (window as any).__NATIVE_API__;
        if (api && api._cbs) {
          api._cbs[cbId] = (payload: any) => {
            if (payload.type === 'install') {
              delete api._cbs[cbId];
              resolve({
                success: Boolean(payload.success),
                message: payload.message,
              });
            }
          };
        }
        try {
          const ret = nb.startInstall(filePath, cbId);
          const obj = JSON.parse(ret);
          if (obj.needPermission) {
            if (api && api._cbs) delete api._cbs[cbId];
            resolve({ success: false, needPermission: true, message: obj.message || '需要开启安装权限' });
          } else if (!obj.success) {
            if (api && api._cbs) delete api._cbs[cbId];
            resolve({ success: false, message: obj.message || '安装启动失败' });
          }
        } catch (e: any) {
          if (api && api._cbs) delete api._cbs[cbId];
          resolve({ success: false, message: e?.message || '安装启动异常' });
        }
      });
    }
  } catch (e: any) {
    console.warn('[AppUpdate] 方案2异常:', e?.message || e);
  }

  // === 方案 3：Capacitor 插件 ===
  try {
    const cap = (window as any).Capacitor;
    const appUpdate = cap?.Plugins?.AppUpdate;
    if (appUpdate && typeof appUpdate.installApk === 'function') {
      console.log('[AppUpdate] ✅ 方案3: Capacitor.Plugins.AppUpdate.installApk()');
      const ret = await appUpdate.installApk({ filePath });
      return {
        success: Boolean(ret?.success),
        needPermission: Boolean(ret?.needPermission),
        message: ret?.message,
      };
    }
  } catch (e: any) {
    console.warn('[AppUpdate] 方案3异常:', e?.message || e);
    throw e;
  }

  throw new Error('未检测到原生安装接口，请重启 App 后重试');
}

/**
 * 取消下载
 */
export async function cancelDownload(): Promise<boolean> {
  try {
    // 方案 1：直接调用 NativeBridge
    const nb = (window as any).NativeBridge;
    if (nb && typeof nb.cancelDownload === 'function') {
      const ret = nb.cancelDownload();
      if (typeof ret === 'string') {
        const obj = JSON.parse(ret);
        return Boolean(obj.success);
      }
      return Boolean(ret?.success);
    }
    // 方案 2：window.__NATIVE_API__
    const nativeApi = (window as any).__NATIVE_API__;
    if (nativeApi && typeof nativeApi.cancelDownload === 'function') {
      const ret = await nativeApi.cancelDownload();
      return Boolean(ret?.success);
    }
    // 方案 3：Capacitor
    const cap = (window as any).Capacitor;
    const appUpdate = cap?.Plugins?.AppUpdate;
    if (appUpdate && typeof appUpdate.cancelDownload === 'function') {
      const ret = await appUpdate.cancelDownload();
      return Boolean(ret?.success);
    }
  } catch (e) { /* ignore */ }
  return false;
}

/**
 * 获取设备 ID（用于管理终端白名单）
 */
export async function getDeviceId(): Promise<DeviceIdInfo> {
  try {
    // 方案 1：直接调用 NativeBridge
    const nb = (window as any).NativeBridge;
    if (nb && typeof nb.getDeviceId === 'function') {
      const ret = nb.getDeviceId();
      const obj = typeof ret === 'string' ? JSON.parse(ret) : ret;
      return {
        deviceId: obj.deviceId,
        deviceName: obj.deviceName || navigator.userAgent.slice(0, 40),
        androidVersion: obj.androidVersion,
        sdkInt: obj.sdkInt,
      };
    }
    // 方案 2：window.__NATIVE_API__
    const nativeApi = (window as any).__NATIVE_API__;
    if (nativeApi && typeof nativeApi.getDeviceId === 'function') {
      const ret = await nativeApi.getDeviceId();
      return {
        deviceId: ret.deviceId,
        deviceName: ret.deviceName || navigator.userAgent.slice(0, 40),
        androidVersion: ret.androidVersion,
        sdkInt: ret.sdkInt,
      };
    }
    // 方案 3：Capacitor
    const cap = (window as any).Capacitor;
    const appUpdate = cap?.Plugins?.AppUpdate;
    if (appUpdate && typeof appUpdate.getDeviceId === 'function') {
      const ret = await appUpdate.getDeviceId();
      return {
        deviceId: ret.deviceId,
        deviceName: ret.deviceName || navigator.userAgent.slice(0, 40),
        androidVersion: ret.androidVersion,
        sdkInt: ret.sdkInt,
      };
    }
  } catch (e) {
    console.warn('[AppUpdate] getDeviceId 异常:', e);
  }

  // 浏览器环境 - 持久 ID
  let id = '';
  try {
    id = localStorage.getItem('__DEVICE_ID__') || '';
  } catch (e) { /* ignore */ }
  if (!id) {
    id = 'web-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
    try { localStorage.setItem('__DEVICE_ID__', id); } catch (e) { /* ignore */ }
  }
  return {
    deviceId: id,
    deviceName: navigator.userAgent.slice(0, 40),
  };
}

// ============================================================
// 后端 API
// ============================================================

/**
 * 检查更新 - 传入本地版本号
 */
export async function checkUpdate(currentVersion: string, platform = 'android'): Promise<UpdateCheckResponse> {
  console.log('%c===== [AppUpdate] checkUpdate 请求 =====', 'color:#2563eb;font-weight:bold;font-size:14px;');
  console.log('[AppUpdate] currentVersion:', currentVersion);
  console.log('[AppUpdate] platform:', platform);

  try {
    const data = await api.post<UpdateCheckResponse>('/app-updates/check', {
      currentVersion,
      platform,
    });
    console.log('%c[AppUpdate] 服务器返回:', 'color:#16a34a;font-weight:bold;', JSON.stringify(data, null, 2));
    if (data && typeof data.hasUpdate !== 'undefined') {
      data.hasUpdate = Boolean(data.hasUpdate);
    }
    console.log('[AppUpdate] 最终 hasUpdate:', data?.hasUpdate);
    return data;
  } catch (e: any) {
    console.error('[AppUpdate] 检查更新失败:', e);
    return {
      hasUpdate: false,
      message: e.message || '检测更新失败',
    };
  }
}

/**
 * 管理后台 - 认证
 */
export async function adminAuth(
  password: string,
  deviceId: string,
  deviceName: string = '',
  isFirst: boolean = false
): Promise<{ success: boolean; message?: string; deviceId?: string; token?: string; needDeviceAuth?: boolean; error?: string }> {
  try {
    const body: any = { password, deviceId };
    if (deviceName) body.deviceName = deviceName;
    if (isFirst) body.isFirst = '1';
    const data = await api.post<any>('/app-updates/admin-auth', body);
    if (data && data.error) return { success: false, error: data.error, needDeviceAuth: data.needDeviceAuth, deviceId: data.deviceId };
    return { success: true, ...data };
  } catch (e: any) {
    return { success: false, error: e.message || '认证失败' };
  }
}

/**
 * 管理后台 - 发布版本
 */
export async function adminPublishVersion(
  creds: { password: string; deviceId: string },
  params: {
    version: string;
    versionCode?: number;
    downloadUrl: string;
    fileSize?: number;
    releaseNotes: string;
    isForce: boolean;
    platform?: string;
    checksum?: string;
  }
): Promise<{ success: boolean; message?: string; version?: string; error?: string }> {
  try {
    const data = await api.post<any>('/app-updates/publish', {
      ...creds,
      ...params,
    });
    if (data && data.error) return { success: false, error: data.error };
    return { success: true, ...data };
  } catch (e: any) {
    return { success: false, error: e.message || '发布失败' };
  }
}

/**
 * 管理后台 - 删除版本
 */
export async function adminDeleteVersion(
  creds: { password: string; deviceId: string },
  id: string
): Promise<{ success: boolean; message?: string; error?: string }> {
  try {
    const data = await api.post<any>('/app-updates/delete', { ...creds, id });
    if (data && data.error) return { success: false, error: data.error };
    return { success: true, ...data };
  } catch (e: any) {
    return { success: false, error: e.message || '删除失败' };
  }
}

/**
 * 管理后台 - 版本列表
 */
export async function adminListVersions(
  creds: { password: string; deviceId: string },
  platform = 'android'
): Promise<any[]> {
  try {
    const data = await api.post<any>('/app-updates/list', { ...creds, platform });
    if (Array.isArray(data)) return data;
    if (data && Array.isArray((data as any).results)) return (data as any).results;
    return [];
  } catch (e) {
    return [];
  }
}

// ============================================================
// 辅助函数
// ============================================================

export function formatBytes(bytes: number): string {
  if (!bytes || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

/**
 * 检查是否运行在原生环境（用于 UI 判断是否显示下载按钮）
 */
export function isNative(): boolean {
  return isAndroidAPK();
}
